import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import {
  // useQuery,
  useMutation,
  useQuery,
} from "@apollo/react-hooks";
import gql from "graphql-tag";
import { tenantDetails } from "../../graphql/queries/tenantQueries";
import {
  updateTenantMutation,
  loginAsMutation,
} from "./../../graphql/mutations/tenantMutations";
import useBaseNavigation from "./../../hooks/baseNavigation";
import useBaseFieldSetters from "./../../hooks/baseFieldSetters";
import useFormErrors from "./../../hooks/formErrors";
import useHyperState from "./../../hooks/hyperState";
import {
  TENANT_MODEL_NAME,
  TENANT_ACCESS_TYPES,
  GENDERS,
  AGE_BRACKETS,
} from "./../../configs/constants";
import PageSpinner from "./../../mood-ui/PageSpinner";
import {
  PageContainer,
  BlockContainer,
  Row,
  Col,
  FormContainer,
  FormData,
} from "./../../mood-ui/Layout";
import {
  FormControls,
  SaveButton,
  CancelButton,
  BasicControl,
} from "./../../mood-ui/Controls";
import { Heading } from "./../../mood-ui/Typography";
import {
  TextField,
  NumberField,
  ToggleCheckboxField,
  SelectField,
} from "./../../mood-ui/Fields";
import { LabelContext } from "../../contexts/LabelContext";
import useDataProvider from "../../hooks/dataProvider";
import useNotification from "../../hooks/notifications";
import {
  ErrorNotification,
  SuccessNotification,
} from "../../mood-ui/Notifications";
import usePermissions from "../../hooks/permissions";
import Permissions, {
  tenantNoAccessFallbackList,
} from "../../configs/permissions";

const DEFAULT_META_STATE = {
  activation_push_enabled: false,
};

const TENANT_DETAILS = gql(tenantDetails);
const UPDATE_TENANT = gql(updateTenantMutation);
const LOGIN_AS = gql(loginAsMutation);

const LandlordTenant = () => {
  const {
    setFieldError,
    formHasErrors,
    fieldError,
    resolveMutationErrors,
    clearError,
  } = useFormErrors();
  const { id: landlordId, uId: id } = useParams();
  const { protectPage, hasAccess } = usePermissions();
  protectPage(Permissions.Query.User, {
    noAccessFallbackList: tenantNoAccessFallbackList,
    variables: { id },
  });
  const labelContext = useContext(LabelContext);

  const [labels] = useDataProvider("labels");

  const { goBack } = useBaseNavigation(UPDATE_TENANT, {
    urlContext: `clients/${landlordId}`,
  });

  const [metaState, setMetaState] = useState(DEFAULT_META_STATE);
  const [createdMethod, setCreatedMethod] = useState("");
  const [parent, setParent] = useState({ id: 0, email: "" });

  const {
    state: formData,
    setState: setFormData,
    stateMethods,
  } = useHyperState("formData", {
    id: 0,
    email: "",
    account_name: "",
    firstname: "",
    lastname: "",
    address: "",
    towncity: "",
    postcode: "",
    telephone: "",
    promo_premium_access: false,
    tenancy_start: "",
    tenancy_end: "",
    notes: "",
    ref: "",
    landlord: { id: "", email: "" },
    landlord_id: "",
    label_id: 0,
  });

  const [loginAs] = useMutation(LOGIN_AS, {
    onCompleted: (result) => {
      window.open(result.loginAsTenant.url, "_blank");
    },
    onError: (e) => {
      displayErrorNotification({
        heading: "Error",
        message: "Failed to create System User Login Data",
        secondsToDisplay: 2,
      });
    },
    update: (cache, { data }) => {
      return false;
    },
  });

  const {
    setFieldValue: handleFieldOnChangeProxied,
    toggleCheckbox: handleToggleCheckbox,
    toggleArrayFieldValue: toggleListValue,
    arrayFieldHasValue: listInclude,
  } = useBaseFieldSetters(formData, stateMethods, clearError);

  const handleFieldOnChange = (e, type = "text") => {
    clearError(e.target.name);
    if (e.target?.checkValidity && !e.target?.checkValidity()) {
      setFieldError(
        e.target.name,
        e.target.title || e.target.validationMessage || "Input has an error",
      );
      setDraft(false);
    } else if (formHasErrors()) {
      setDraft(false);
    } else {
      setDraft(true);
    }
    handleFieldOnChangeProxied(e, type);
  };

  const {
    notify: displayNotification,
    notificationState,
    handleClose,
  } = useNotification();

  const {
    notify: displayErrorNotification,
    notificationState: errorNotificationState,
    handleClose: handleCloseError,
  } = useNotification();

  const {
    loading,
    error,
    refetch: refetchTenant,
  } = useQuery(TENANT_DETAILS, {
    variables: {
      id,
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (result) => {
      if (!result || !result.tenant) return false;
      setFormData({
        ...result.tenant,
        landlord_id: result.tenant.landlord.id,
        label_id: result.tenant.label_type.id,
      });
      setParent(result.tenant.landlord);
      setCreatedMethod(result.tenant.created_method);
      setMetaState({ ...result.tenant.meta_state });
    },
  });

  const [update, { loading: isUpdating }] = useMutation(UPDATE_TENANT, {
    onCompleted: (result) => {
      // displayNotification({
      //   heading: "Update OK",
      //   message: "Successfully updated user",
      //   secondsToDisplay: 1,
      // });
      goBack();
    },
    onError: (e) => {
      displayErrorNotification({
        heading: "Error",
        message: "Failed to update",
        secondsToDisplay: 2,
      });
      resolveMutationErrors(e);
    },
  });

  const handleSave = () => {
    const data = {
      ...formData,
      landlord_id: parseInt(formData.landlord_id),
      label_id: parseInt(formData.label_id),
    };

    update({
      variables: {
        ...data,
      },
    });
  };

  const handleLoginAs = () => {
    loginAs({ variables: { account_id: parseInt(id) } });
  };

  const [isDraft, setDraft] = useState(false);

  if (!formData) return <PageSpinner />;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={8}>
            {loading && (
              <Heading
                inset={true}
                text="Fetching user data..."
                description=""
              />
            )}
            {!loading && (
              <Heading
                inset={true}
                text={`Update user`}
                description={`Update user. ID: ${formData.id}  Email:  ${formData.email}`}
              />
            )}
          </Col>
          <Col width={4} extraStyles="flex justify-end items-center">
            <BasicControl onClick={handleLoginAs} label="Login as this User" />
          </Col>
        </Row>
        <FormContainer>
          <FormData>
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="User settings"
                      description="Communication and membership information."
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                <Row>
                  <Col width={4} extraStyles="">
                    <TextField
                      loading={loading}
                      name="firstname"
                      label="First name"
                      value={formData.firstname}
                      onChange={(e) => {
                        handleFieldOnChange(e);
                      }}
                      error={fieldError("firstname")}
                      isRequired={true}
                      minlength={1}
                      maxlength={64}
                      description=""
                      autocomplete="off"
                    />
                  </Col>
                  <Col width={4} extraStyles="">
                    <TextField
                      loading={loading}
                      name="lastname"
                      label="Last name"
                      value={formData.lastname}
                      onChange={(e) => {
                        handleFieldOnChange(e);
                      }}
                      error={fieldError("lastname")}
                      isRequired={true}
                      minlength={1}
                      maxlength={64}
                      description=""
                      autocomplete="off"
                    />
                  </Col>
                  <Col width={4} extraStyles="">
                    <TextField
                      loading={loading}
                      name="email"
                      label="Email"
                      value={formData.email}
                      onChange={(e) => {
                        handleFieldOnChange(e);
                      }}
                      error={fieldError("email")}
                      isRequired={true}
                      minlength={1}
                      maxlength={64}
                      description="Must be unique"
                      autocomplete="off"
                    />
                  </Col>
                  <Col width={4}>
                    <SelectField
                      loading={loading}
                      value={formData.label_id}
                      values={labels}
                      label="Label"
                      name="label_id"
                      onChange={(e) => handleFieldOnChange(e, "int")}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("label_id")}
                      isRequired={true}
                      description=""
                    />
                  </Col>
                  <Col width={4}>
                    <SelectField
                      loading={loading}
                      value={formData.access_type}
                      values={TENANT_ACCESS_TYPES}
                      label="Access Type"
                      name="access_type"
                      onChange={handleFieldOnChange}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("access_type")}
                      isRequired={true}
                      description="Fees are client-specific"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="Contact information"
                      description=""
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                <Row>
                  <Col width={3}>
                    <SelectField
                      loading={loading}
                      value={formData.gender}
                      values={GENDERS}
                      label="Gender"
                      name="gender"
                      onChange={handleFieldOnChange}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("gender")}
                    />
                  </Col>
                  <Col width={3}>
                    <SelectField
                      loading={loading}
                      value={formData.age_range}
                      values={AGE_BRACKETS}
                      label="Age Range"
                      name="age_range"
                      onChange={handleFieldOnChange}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("age_range")}
                    />
                  </Col>
                  <Col width={2}> </Col>

                  <Col width={6}>
                    <TextField
                      loading={loading}
                      name="address"
                      label="Address"
                      value={formData.address}
                      onChange={handleFieldOnChange}
                      error={fieldError("address")}
                    />
                  </Col>
                  <Col width={6}>
                    <TextField
                      loading={loading}
                      name="towncity"
                      label="Towncity"
                      value={formData.towncity}
                      onChange={handleFieldOnChange}
                      error={fieldError("towncity")}
                    />
                  </Col>
                  <Col width={6}>
                    <TextField
                      loading={loading}
                      name="postcode"
                      label="Postcode"
                      value={formData.postcode}
                      onChange={handleFieldOnChange}
                      error={fieldError("postcode")}
                    />
                  </Col>
                  <Col width={6}>
                    <TextField
                      loading={loading}
                      name="telephone"
                      label="Telephone"
                      value={formData.telephone}
                      onChange={handleFieldOnChange}
                      error={fieldError("telephone")}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </FormData>
          <FormControls>
            <CancelButton onClick={goBack} />
            <SaveButton
              onClick={handleSave}
              disabled={!isDraft || formHasErrors()}
              loading={isUpdating}
            />
          </FormControls>
        </FormContainer>
      </BlockContainer>
      <SuccessNotification {...notificationState} onClose={handleClose} />
      <ErrorNotification
        {...errorNotificationState}
        onClose={handleCloseError}
      />
    </PageContainer>
  );
};

export default LandlordTenant;
